/**
 * Merchant Pro V1 PR-3 — Walk-in / Waitlist console.
 *
 * Route: /owner/waitlist
 *
 * Backed by:
 *   - GET  /api/owner/waitlists/buckets?tab=now|upcoming|history   (V3-E1)
 *   - POST /api/cashier/waitlists                                  (V3-A2)
 *   - POST /api/cashier/waitlists/{id}/seat                        (V3-A3)
 *
 * Operator-locked discipline:
 *   - NO bulk actions / NO row checkboxes.
 *   - Reuses StatusPill / PageHeader / SectionCard / FullSpinner /
 *     QueryError / EmptyState primitives (PR-2 visual language).
 *   - Q2: bottom-stats card resets at brand-local midnight (Asia/Jakarta),
 *     handled server-side by V3-E1; the client just renders.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, LogIn, Users, Clock, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { classes } from '@/lib/utils';
import RequirePermission from '@/lib/capabilities';
import ChairGridTableCard from '@/components/owner/ChairGridTableCard';

import {
  PageHeader,
  SectionCard,
  StatusPill,
  FullSpinner,
  QueryError,
  EmptyState,
  Spinner,
} from '@/components/ui_primitives/Misc';

const STATUS_PILL_VARIANT = {
  queued: 'amber',
  notified: 'blue',
  seated: 'green',
  expired: 'slate',
  cancelled_by_customer: 'red',
  cancelled_by_owner: 'red',
  cancelled_by_system: 'red',
  confirmed: 'gold',
};

function statusPill(status, testID) {
  const variant = STATUS_PILL_VARIANT[status] || 'slate';
  const label = (status || '').replace(/_/g, ' ');
  return (
    <StatusPill variant={variant} testID={testID}>
      {label}
    </StatusPill>
  );
}

const TABS = [
  { value: 'now', label: 'Now' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'history', label: 'History' },
];

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return iso; }
}

function StatTile({ icon, label, value, testID, accent = false }) {
  return (
    <div
      className={
        accent
          ? 'flex-1 min-w-[180px] bg-gradient-to-br from-gold/15 via-gold/5 to-transparent border border-gold/30 rounded-2xl p-5 shadow-lg'
          : 'flex-1 min-w-[180px] bg-[#15151B]/80 border border-white/10 rounded-2xl p-5'
      }
      data-testid={testID}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-white/55">
        <span className="text-gold">{icon}</span>
        {label}
      </div>
      <div className={
        accent
          ? 'text-3xl font-display font-bold text-gold mt-3 tabular-nums tracking-tight'
          : 'text-3xl font-display font-bold text-white mt-3 tabular-nums tracking-tight'
      }>
        {value}
      </div>
    </div>
  );
}

/* PR-4c — wait-time computation for the row chip + pulse. */
function computeWaitMinutes(enqueuedAtIso) {
  if (!enqueuedAtIso) return null;
  const enq = new Date(enqueuedAtIso).getTime();
  if (Number.isNaN(enq)) return null;
  return Math.max(0, Math.floor((Date.now() - enq) / 60_000));
}

function waitChipTone(min) {
  if (min == null) return { cls: 'bg-white/5 border-white/15 text-white/60', tag: '—' };
  if (min < 10) return { cls: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300', tag: `${min}m` };
  if (min < 20) return { cls: 'bg-amber-500/15 border-amber-500/40 text-amber-300', tag: `${min}m` };
  if (min < 30) return { cls: 'bg-orange-500/15 border-orange-500/40 text-orange-300', tag: `${min}m` };
  return { cls: 'bg-red-500/15 border-red-500/40 text-red-300', tag: `${min}m` };
}

/* -------------------- Add Walk-in modal -------------------- */

function AddWalkInModal({ onClose, onSubmit, branches, isPending }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [branchId, setBranchId] = useState(branches?.[0]?.id || '');

  const submit = () => {
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    if (!branchId) {
      toast.error('Branch is required.');
      return;
    }
    onSubmit({
      walk_in_customer: { name: name.trim(), phone: phone.trim() || undefined },
      branch_id: branchId,
      party_size: Math.max(1, Math.min(50, Number(partySize) || 1)),
      idempotency_key: `walkin-${crypto.randomUUID()}`,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4"
      data-testid="add-walkin-modal"
    >
      <div className="bg-[#15151B] border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <h3 className="font-display text-lg font-semibold text-white mb-1">Add walk-in</h3>
        <p className="text-sm text-white/55 mb-4">
          Place a customer onto the waitlist queue from the host stand.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">
              Customer name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Andre Wijaya"
              data-testid="walkin-name-input"
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">
              Phone (optional)
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+62…"
              data-testid="walkin-phone-input"
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">
                Party size
              </label>
              <input
                type="number"
                min="1" max="50"
                value={partySize}
                onChange={(e) => setPartySize(e.target.value)}
                data-testid="walkin-party-input"
                className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-gold/40 tabular-nums"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">
                Branch
              </label>
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                data-testid="walkin-branch-select"
                className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-gold/40"
              >
                {(branches || []).map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="walkin-cancel-btn"
            className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={isPending}
            data-testid="walkin-submit-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gold/40 bg-gold/15 text-gold hover:bg-gold/25 disabled:opacity-50"
          >
            {isPending ? <Spinner className="w-4 h-4" /> : <Plus size={14} />}
            Add to waitlist
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Page -------------------- */

export default function WalkInWaitlistPage() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const brandId = user?.brand_id;
  const queryClient = useQueryClient();

  const [tab, setTab] = useState('now');
  const [showAddModal, setShowAddModal] = useState(false);

  const branchesQ = useQuery({
    queryKey: qk.brandBranches(brandId || 'self'),
    queryFn: () => api.brandBranches(token, brandId),
    enabled: !!token && !!brandId,
    staleTime: 5 * 60_000,
  });

  const bucketsQ = useQuery({
    queryKey: qk.ownerWaitlistBuckets({ tab }),
    queryFn: () => api.ownerWaitlistBuckets(token, { tab, limit: 50 }),
    enabled: !!token,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const addM = useMutation({
    mutationFn: (body) => api.cashierWaitlistAddWalkIn(token, body),
    onSuccess: () => {
      toast.success('Walk-in added to waitlist.');
      setShowAddModal(false);
      queryClient.invalidateQueries({ queryKey: ['owner', 'waitlists'] });
    },
    onError: (err) =>
      toast.error(err?.detail?.message || err?.message || 'Add walk-in failed.'),
  });

  const seatM = useMutation({
    mutationFn: ({ waitlistId }) =>
      api.cashierWaitlistSeat(token, waitlistId, {
        duration_min: 60,
        idempotency_key: `seat-${crypto.randomUUID()}`,
      }),
    onSuccess: (r) => {
      toast.success(`Seated · booking code ${r.booking_code || ''}`);
      queryClient.invalidateQueries({ queryKey: ['owner', 'waitlists'] });
      queryClient.invalidateQueries({ queryKey: ['owner', 'dashboard', 'today'] });
    },
    onError: (err) =>
      toast.error(err?.detail?.message || err?.message || 'Seat failed.'),
  });

  const items = bucketsQ.data?.items ?? [];
  const stats = bucketsQ.data?.stats ?? {};
  const tz = bucketsQ.data?.brand_timezone;

  return (
    <div className="space-y-6" data-testid="walkin-waitlist-page">
      <PageHeader
        kicker="Operations"
        title="Walk-in & Waitlist"
        subtitle={`Add walk-ins, promote queued entries, seat customers. Today resets at brand-local midnight${tz ? ` (${tz})` : ''}.`}
        actions={
          /* Sub-phase RBAC-C2 (2026-06-18) — Add Walk-in gated on
             `waitlist.add_walk_in`. Hidden for any role missing the
             cap (no role in C1 seed currently lacks it, but the wrap
             is in place for future custom-role configurations). */
          <RequirePermission cap="waitlist.add_walk_in">
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              data-testid="add-walkin-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gold/40 bg-gold/15 text-gold hover:bg-gold/25"
            >
              <Plus size={14} /> Add Walk-in
            </button>
          </RequirePermission>
        }
      />

      {/* Tabs */}
      <SectionCard testID="waitlist-tabs-card" padded={false} className="px-4 py-3">
        <div
          className="inline-flex p-1 bg-black/40 border border-white/8 rounded-xl"
          data-testid="waitlist-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              data-testid={`waitlist-tab-${t.value}`}
              className={classes(
                'px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors',
                tab === t.value
                  ? 'bg-gold/20 text-gold border border-gold/30'
                  : 'text-white/60 hover:text-white border border-transparent',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </SectionCard>

      {/* Table */}
      <SectionCard testID="waitlist-table-card" padded={false}>
        {bucketsQ.isLoading ? (
          <FullSpinner />
        ) : bucketsQ.isError ? (
          <div className="p-6">
            <QueryError error={bucketsQ.error} onRetry={bucketsQ.refetch} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No entries"
            subtitle="Add a walk-in or wait for queued customers to surface here."
            testID="waitlist-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-widest font-semibold text-white/40 border-b border-white/10">
                  <th className="text-left px-5 py-3 font-semibold w-12">Table</th>
                  <th className="text-left px-5 py-3 font-semibold">Pos</th>
                  <th className="text-left px-3 py-3 font-semibold">Customer</th>
                  <th className="text-left px-3 py-3 font-semibold">Party</th>
                  <th className="text-left px-3 py-3 font-semibold">Branch</th>
                  <th className="text-left px-3 py-3 font-semibold">Enqueued</th>
                  <th className="text-left px-3 py-3 font-semibold">Status</th>
                  <th className="text-right px-5 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const waitMin = computeWaitMinutes(row.enqueued_at);
                  const chip = waitChipTone(waitMin);
                  const isActive = ['queued', 'notified'].includes(row.status);
                  const isCritical = isActive && waitMin != null && waitMin >= 25;
                  /* E2 PR4d-4 — Inline chair-grid thumbnail slot. The
                   * waitlist payload does NOT carry a `target_table_id`
                   * today (waitlist → reservation linkage is via
                   * `promoted_to_reservation_id` which would require an
                   * N+1 fetch to resolve a table at the row level). For
                   * V1 every waitlist row renders the gray-dashed
                   * placeholder. A future enhancement could surface the
                   * target table via a backend $lookup against
                   * reservations on `promoted_to_reservation_id` (filed
                   * as part of P2-LIST-STALE-AFTER-MOVE-A backlog
                   * follow-up). The 32-px footprint + placeholder
                   * conform to the operator's PR4d-4 spec. */
                  const thumbTable = row.target_table_id ? {
                    id:       row.target_table_id,
                    name:     '',
                    capacity: row.party_size ?? 1,
                    state:    row.status === 'seated' ? 'checked_in' : 'reserved',
                  } : null;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`waitlist-row-${row.id}`}
                      data-wait-minutes={waitMin ?? ''}
                      className={classes(
                        'border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] transition-colors',
                        isCritical && 'animate-pulse-row',
                      )}
                    >
                      <td className="px-5 py-3">
                        <div
                          data-testid={`waitlist-table-thumb-${row.id}`}
                          className="w-8 h-8 grid place-items-center"
                        >
                          {thumbTable ? (
                            <div className="w-8 h-8">
                              <ChairGridTableCard
                                table={thumbTable}
                                size="xs"
                                showStatePill={false}
                                showLegendIcon={false}
                                testIdPrefix={`waitlist-thumb-${row.id}`}
                              />
                            </div>
                          ) : (
                            <div
                              data-testid={`waitlist-table-thumb-${row.id}-placeholder`}
                              className="w-8 h-8 rounded-xl border border-white/20 border-dashed bg-white/[0.02]"
                              aria-label="No table assigned"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gold/85 font-mono tabular-nums">
                        {row.position_in_queue ?? '—'}
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-white font-medium truncate max-w-[220px]">
                          {row.customer_name || '—'}
                        </div>
                        <div className="text-[11px] text-white/40 font-mono mt-0.5">
                          {row.customer_phone || ''}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-white/80 tabular-nums">
                        {row.party_size}
                      </td>
                      <td className="px-3 py-3 text-white/80">{row.branch_name || '—'}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-white/60 text-xs">
                            {formatTime(row.enqueued_at)}
                          </span>
                          {isActive ? (
                            <span
                              data-testid={`wl-wait-chip-${row.id}`}
                              className={classes(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold tabular-nums uppercase tracking-wider',
                                chip.cls,
                              )}
                            >
                              <Clock size={9} />
                              {chip.tag}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {statusPill(row.status, `wl-status-${row.id}`)}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {tab !== 'history' && isActive ? (
                          <RequirePermission
                            cap="waitlist.seat"
                            fallback={<span className="text-xs text-white/30">—</span>}
                          >
                            <button
                              type="button"
                              onClick={() => seatM.mutate({ waitlistId: row.id })}
                              disabled={seatM.isPending}
                              data-testid={`seat-now-${row.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gold/40 bg-gold/15 text-gold hover:bg-gold/25 disabled:opacity-50 whitespace-nowrap"
                            >
                              <LogIn size={12} /> Seat Now
                            </button>
                          </RequirePermission>
                        ) : (
                          <span className="text-xs text-white/30">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Bottom stats — premium tile-row (PR-4c — 4c-AC#4) */}
      <SectionCard testID="waitlist-stats-card">
        <div className="flex flex-wrap items-stretch gap-3">
          <StatTile
            icon={<Users size={14} />}
            label="Total waitlist"
            value={stats.total_waitlist ?? 0}
            testID="stat-total-waitlist"
            accent
          />
          <StatTile
            icon={<Clock size={14} />}
            label="Avg wait (today)"
            value={`${stats.avg_wait_minutes ?? 0}m`}
            testID="stat-avg-wait"
          />
          <StatTile
            icon={<BarChart3 size={14} />}
            label="Seated from waitlist (today)"
            value={stats.seated_from_waitlist_today ?? 0}
            testID="stat-seated-today"
          />
        </div>
      </SectionCard>

      {showAddModal ? (
        <AddWalkInModal
          onClose={() => setShowAddModal(false)}
          onSubmit={(body) => addM.mutate(body)}
          branches={branchesQ.data || []}
          isPending={addM.isPending}
        />
      ) : null}
    </div>
  );
}
